import {
  ref, computed, watch,
} from 'vue';
import { useStore } from 'vuex';
import { watchOnce } from '@vueuse/core';
import { useUpc } from '@modules/upc';
import { useBrand } from '@composables/useBrand';
import { useProduct } from '@modules/product';
import { useFeatureEligibility, useProductFlags } from '@modules/featureControl';
import { useFulfilmentData } from './useFulfilmentData';
import { pickupStoreParser, productInfoParser } from '../utils';
import { GET_ZIP_CODE_FROM_STORE } from '../../../types/getters';

/**
 * @typedef {{
 * availabilityDataParam?: import('../types').bopsAvailabilityXapiResponce,
 * selectedUpcIdParam?: string,
 * selectedRadioButtonParam?: string,
 * }} parseParams
 */

/**
 * @returns {{
 * parseAvailabilityData: (param?: parseParams) => void,
 * isAvailabilityEnabled: import('vue').ComputedRef<boolean>,
 * pickupStore: import('vue').Ref<import('../types').PickupStore | {}>,
 * productInfo: import('vue').Ref<import('../types').ProductInfo>,
 * }}
 */
export const useAvailability = () => {
  const store = useStore();
  const { isMcom } = useBrand();
  const { productData: product } = useProduct();
  const { isInternationalMode } = useFeatureEligibility();
  const { isCountryEligibleFlag, isBigTicketItemFlag } = useProductFlags();
  const { selectedUpcId, selectedUpcNumber, upcData } = useUpc();

  const { zipCode, storeLocation, selectedRadioButton } = useFulfilmentData();
  const showImmediately = ref(!!selectedUpcNumber.value);
  const isAvailabilityEnabledMcom = computed(() => showImmediately.value && isCountryEligibleFlag && !isInternationalMode && !isBigTicketItemFlag);
  const isAvailabilityEnabledBcom = computed(() => showImmediately.value && isCountryEligibleFlag && !isBigTicketItemFlag);
  const isAvailabilityEnabled = computed(() => (isMcom ? isAvailabilityEnabledMcom.value : isAvailabilityEnabledBcom.value));
  const availabilityData = computed(() => store.state.availabilityStore.data);
  const zipCodeFromStore = computed(() => store.getters[`availabilityStore/${GET_ZIP_CODE_FROM_STORE}`]);

  const pickupStore = ref({});
  const productInfo = ref(productInfoParser({
    selectedRadioButton: selectedRadioButton.value,
    availabilityData: availabilityData.value,
    upcData: upcData.value,
    product,
  }));

  function parseAvailabilityData({
    availabilityDataParam = availabilityData.value,
    selectedUpcIdParam = selectedUpcId.value,
    selectedRadioButtonParam = selectedRadioButton.value,
  } = {}) {
    pickupStore.value = pickupStoreParser({
      availabilityData: availabilityDataParam,
      storeId: storeLocation.value,
    });

    productInfo.value = productInfoParser({
      selectedRadioButton: selectedRadioButtonParam,
      availabilityData: availabilityDataParam,
      selectedUpcId: selectedUpcIdParam,
      upcData: upcData.value,
      product,
    });
  }

  watch(selectedUpcId, (newSelectedUpcId) => parseAvailabilityData({ selectedUpcIdParam: newSelectedUpcId }));

  watch(availabilityData, (newAvailabilityData) => parseAvailabilityData({ availabilityDataParam: newAvailabilityData }));

  watch(selectedRadioButton, (newSelectedRadioButton) => parseAvailabilityData({ selectedRadioButtonParam: newSelectedRadioButton }));

  watchOnce(selectedUpcId, () => {
    showImmediately.value = true;
  });

  watchOnce(zipCodeFromStore, (newValue) => {
    if (!zipCode.value) {
      zipCode.value = newValue;
    }
  });

  return {
    parseAvailabilityData,
    isAvailabilityEnabled,
    pickupStore,
    productInfo,
  };
};

export default useAvailability;
